import { useContext, useMemo } from 'react';

import { Divider, withProps } from '@cast/design-system';
import { WorkloadRuntime } from '@cast/types';

import { useIsCustomMetricsEnabled } from 'features/cluster/workload-optimization/_hooks/permissions/useIsCustomMetricsEnabled';
import { useShowHpaV2 } from 'features/cluster/workload-optimization/_hooks/permissions/useShowHpaV2';
import {
  useIsWoopDemoMode,
  usePaginatedOptimizationWorkloads,
} from 'features/workload-optimization';

import {
  PredictiveQuickSearch,
  ConfiguredViaQuickSearch,
  OptimizedQuickSearch,
  HpaAvailabilityQuickSearch,
  JvmQuickSearch,
  VerticalOptimizationQuickSearch,
  HorizontalOptimizationQuickSearch,
} from './_components';
import { hasPredictiveWorkloads } from '../../../../_utils';
import { KindQuickSearch } from '../../../../_workloads-list/components/KindQuickSearch';
import { OptimizationWorkloadsListContext } from '../../../context';

import { QuickSearch, QuickSearchGroup } from '@cast/shared-search-ui';

const StyledDivider = withProps(Divider, {
  orientation: 'vertical',
  color: 'grey.300',
  sx: { mx: 4, height: 24, alignSelf: 'flex-end' },
});

export const WoopQuickSearch = () => {
  const { workloads } = useContext(OptimizationWorkloadsListContext);

  const showPredictiveFilter = useMemo(
    () => hasPredictiveWorkloads(workloads),
    [workloads]
  );

  const isCustomMetricsEnabled = useIsCustomMetricsEnabled();
  const { workloads: jvmWorkloads } = usePaginatedOptimizationWorkloads({
    anyContainerWithRuntime: WorkloadRuntime.JVM,
    pageLimit: 1,
    enabled: isCustomMetricsEnabled,
  });
  const isWoopDemoMode = useIsWoopDemoMode();
  const showJvmFilter =
    !isWoopDemoMode && isCustomMetricsEnabled && jvmWorkloads.length > 0;

  const { showHpaV2 } = useShowHpaV2();

  return (
    <QuickSearch defaultOpen>
      {showHpaV2 && (
        <>
          <QuickSearchGroup label="Vertical">
            <VerticalOptimizationQuickSearch />
          </QuickSearchGroup>
          <StyledDivider />
          <QuickSearchGroup label="Horizontal">
            <HorizontalOptimizationQuickSearch />
          </QuickSearchGroup>
          <StyledDivider />
        </>
      )}
      {(showPredictiveFilter || showJvmFilter) && (
        <>
          {showPredictiveFilter && <PredictiveQuickSearch />}
          {showJvmFilter && <JvmQuickSearch />}
          <StyledDivider />
        </>
      )}
      <QuickSearchGroup label="Configured by">
        <ConfiguredViaQuickSearch />
      </QuickSearchGroup>
      {!showHpaV2 && (
        <>
          <StyledDivider />
          <OptimizedQuickSearch />
          <StyledDivider />
          <HpaAvailabilityQuickSearch />
        </>
      )}
      <StyledDivider />
      <QuickSearchGroup label="Kind">
        <KindQuickSearch />
      </QuickSearchGroup>
    </QuickSearch>
  );
};
